if (!customElements.get('bstore-product-info')) {
  customElements.define('bstore-product-info', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.page = this.closest('product-page');
      this.measurementMethod = this.dataset.measurementMethod || 'unverified';
      const methodLabels = {
        garment_circumference: 'Garment measurements (full circumference; length measured separately).',
        garment_flat: 'Flat garment measurements. Compare with a similar well-fitting garment laid flat; do not double widths.',
        body: 'Body measurements. Compare your body measurements directly with this guide; do not double them.',
        unverified: 'Measurement method needs confirmation. Call us at 239-919-3446 before choosing your size.'
      };
      this.methodLabel = methodLabels[this.measurementMethod] || methodLabels.unverified;
      const source = this.querySelector('[data-description]').content.cloneNode(true);
      // Remove composition before content is split into either dialog.
      source.querySelectorAll('li,p,td,dd').forEach(el => {
        if (el.children.length) return;
        if (/\d+(?:\.\d+)?\s*%/.test(el.textContent) && /\b(polyester|cotton|viscose|rayon|nylon|spandex|elastane|wool|acrylic|silk|linen)\b/i.test(el.textContent)) {
          const parts = el.textContent.split(/[;|]/);
          const kept = parts.filter(part => !/\d+(?:\.\d+)?\s*%/.test(part)).join('; ').trim();
          if (kept) el.textContent = kept;
          else el.remove();
        }
      });
      const composition = /^(?:(?:material|fabric|fibre|fiber)\s*(?:composition|content)?|composition|country of origin|origin)\s*:/i;
      const origin = /^(?:imported\b|made in\b|manufactured in\b|country of origin\b)/i;
      const fibrePercent = /\b\d+(?:\.\d+)?\s*%\s*(?:polyester|cotton|viscose|rayon|nylon|polyamide|acrylic|wool|cashmere|silk|linen|spandex|elastane|modal|lyocell|bamboo|polyurethane|polyvinyl|leather)\b/i;
      Array.from(source.querySelectorAll('li,p,tr,dt,dd')).forEach(el => {
        if (el.querySelector('li,p,tr,dt,dd,table')) return;
        const text = el.textContent.trim();
        if (composition.test(text) || origin.test(text) || fibrePercent.test(text)) {
          if (el.tagName === 'DT' && el.nextElementSibling?.tagName === 'DD') el.nextElementSibling.remove();
          el.remove();
        }
      });
      Array.from(source.querySelectorAll('h1,h2,h3,h4,h5,h6')).forEach(el => {
        if (/^(?:(?:material|fabric|fibre|fiber)\s*(?:composition|content)?|composition|origin|country of origin)\s*:?$/i.test(el.textContent.trim())) {
          const next = el.nextElementSibling;
          if (next && !/^H[1-6]$/.test(next.tagName)) next.remove();
          el.remove();
        }
      });
      source.querySelectorAll('ul,ol').forEach(el => { if (!el.textContent.trim()) el.remove(); });
      const sizeStart = Array.from(source.querySelectorAll('h1,h2,h3,h4,h5,h6,p')).find(el => /^(size\s*(guide|chart)|sizing\s*(guide|chart)|size\s*&\s*fit|fit\s*guide)\s*[:–—-]?\s*$/i.test(el.textContent.trim()));
      const features = this.querySelector('[data-info-body="features"]');
      const size = this.querySelector('[data-info-body="size"]');
      if (sizeStart) {
        const range = document.createRange();
        range.setStartBefore(sizeStart);
        range.setEnd(source, source.childNodes.length);
        size.append(range.extractContents());
        this.hasSize = true;
      }
      if (!this.hasSize) {
        const tables = Array.from(source.querySelectorAll('table')).filter(table => /\b(size|bust|waist|hip|inseam|chest)\b/i.test(table.querySelector('tr')?.textContent || ''));
        if (tables.length) {
          tables.forEach(table => size.append(table));
          this.hasSize = true;
        }
      }
      const fitStart = Array.from(source.querySelectorAll('h1,h2,h3,h4,h5,h6,p')).find(el => /^(fit\s*notes?|fit\s*(and|&)\s*sizing|sizing\s*notes?)\s*[:–—-]?\s*$/i.test(el.textContent.trim()));
      if (fitStart) {
        const fitEnd = Array.from(source.querySelectorAll('h1,h2,h3,h4,h5,h6')).find(el => (fitStart.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) && !fitStart.contains(el));
        const range = document.createRange();
        range.setStartBefore(fitStart);
        if (fitEnd) range.setEndBefore(fitEnd);
        else range.setEnd(source, source.childNodes.length);
        size.prepend(range.extractContents());
        this.hasSize = true;
      } else if (!/fit\s*notes?/i.test(size.textContent)) {
        const fitItems = Array.from(source.querySelectorAll('li')).filter(el => /\b(relaxed|oversized|slim[ -]fit|regular[ -]fit|true to size|runs? (small|large)|dropped shoulders|stretchy|stretch:|non[ -]stretch|slight stretch|moderate stretch|high stretch|elastic(ated)? waist)\b/i.test(el.textContent));
        if (fitItems.length) {
          const heading = document.createElement('h3');
          heading.textContent = this.dataset.fitLabel;
          const list = document.createElement('ul');
          fitItems.forEach(el => list.append(el.cloneNode(true)));
          size.prepend(heading, list);
          this.hasSize = true;
        }
      }
      const methodGuide = size.querySelector('.bstore-fit-guide');
      if (methodGuide && this.measurementMethod !== 'garment_circumference') {
        methodGuide.querySelector('.bstore-fit-guide__drawing')?.remove();
        methodGuide.querySelector('.bstore-fit-guide__example')?.remove();
        methodGuide.querySelector('.bstore-fit-guide__grid')?.remove();
        methodGuide.querySelectorAll('.bstore-fit-guide__note').forEach(el => el.remove());
        methodGuide.querySelector('.bstore-fit-guide__header > p:last-child').textContent = 'We source our products from around the world, so sizing may differ from standard US sizes.';
        methodGuide.querySelectorAll('h4').forEach((el, index) => { if (index > 0) el.remove(); });
      }
      if (methodGuide) {
        size.querySelectorAll('p').forEach(paragraph => {
          if (paragraph.closest('.bstore-fit-guide')) return;
          const original = paragraph.textContent.trim();
          if (!/^Garment measurements in inches\./.test(original)) return;
          paragraph.textContent = original
            .replace(/^Garment measurements in inches\./, 'Measurements in inches.')
            .replace(/\s*(?:Bust|Upper Garment Measurement) is measured underarm seam to underarm seam, then doubled\./i, '')
            .replace(/\s*Compare with a similar well-fitting garment\./i, '');
        });
      }
      const linearHeaders = Array.from(size.querySelector('table tr')?.children || []).map(el => el.textContent.trim()).filter(label => /length|shoulder|inseam|rise/i.test(label));
      if (this.measurementMethod === 'garment_circumference') {
        this.methodLabel = linearHeaders.length
          ? 'Garment measurements (full circumference; ' + linearHeaders.join(', ').toLowerCase() + ' measured separately).'
          : 'Garment measurements (full circumference).';
      }
      // Recognize standardized descriptions before adding the theme's method and safeguard.
      size.querySelectorAll('p').forEach(el => {
        const text = el.textContent.trim();
        if (/^Measurement Method:/i.test(text)) el.classList.add('bstore-method-summary');
        if (text.startsWith('If this guide does not clearly identify the measurement method,')) el.classList.add('bstore-method-safeguard');
      });
      const productMethodSummary = size.querySelector('.bstore-method-summary')?.textContent.trim();
      if (this.measurementMethod === 'unverified' && productMethodSummary) {
        this.methodLabel = productMethodSummary.replace(/^Measurement Method:\s*/i, '');
      }
      size.querySelectorAll('.bstore-method-summary').forEach(el => el.remove());
      const methodSummary = document.createElement('p');
      methodSummary.className = 'bstore-method-summary';
      const methodStrong = document.createElement('strong');
      methodStrong.textContent = 'Measurement Method: ' + this.methodLabel;
      methodSummary.append(methodStrong);
      if (this.hasSize) {
        const sizeHeading = Array.from(size.querySelectorAll('h1,h2,h3,h4,h5,h6')).find(el => /^size\s*(guide|chart)\s*:?$/i.test(el.textContent.trim()));
        if (sizeHeading) sizeHeading.after(methodSummary);
        else size.prepend(methodSummary);
      }
      if (size.querySelector('.bstore-fit-guide') && this.measurementMethod === 'garment_circumference') {
        size.querySelectorAll('table').forEach(table => {
          table.querySelectorAll('th,td').forEach(cell => {
            if (!/^bust(?:\\s*\\([^)]*\\))?$/i.test(cell.textContent.trim())) return;
            const units = cell.textContent.match(/\\([^)]*\\)/)?.[0] || '';
            cell.replaceChildren(document.createTextNode('Upper Garment'), document.createElement('br'), document.createTextNode('Measurement' + (units ? ' ' + units : '')));
          });

        });
      }
      if (size.querySelector('.bstore-fit-guide')) {
        const wording = document.createTreeWalker(size, NodeFilter.SHOW_TEXT);
        let textNode;
        while ((textNode = wording.nextNode())) {
          textNode.textContent = textNode.textContent.replace(/\b(bust|chest)\b/gi, this.measurementMethod === 'body' ? 'Upper Body Measurement' : this.measurementMethod === 'garment_flat' ? 'Upper Garment Width' : 'Upper Garment Measurement');
        }
      }
      if (this.measurementMethod === 'body' || this.measurementMethod === 'garment_flat') {
        size.querySelectorAll('th,td').forEach(cell => {
          if (/^(bust|chest)(?:\\s*\\([^)]*\\))?$/i.test(cell.textContent.trim())) {
            cell.textContent = this.measurementMethod === 'body' ? 'Upper Body Measurement' : 'Upper Garment Width';
          }
        });
      }
      if (this.hasSize && !size.querySelector('.bstore-method-safeguard')) {
        const methodSafeguard = document.createElement('p');
        methodSafeguard.className = 'bstore-method-safeguard';
        const safeguardText = document.createElement('strong');
        safeguardText.textContent = 'If this guide does not clearly identify the measurement method, or any instructions appear inconsistent, please call us at 239-919-3446 before choosing your size. We’ll be happy to help.';
        methodSafeguard.append(safeguardText);
        size.append(methodSafeguard);
      }
      const intro = Array.from(source.querySelectorAll('p')).find(el => el.textContent.trim().length > 60 && !el.querySelector('table') && !/^(features|material|care instructions|i\.?c\.?[’']?s styling)/i.test(el.textContent.trim()));
      if (intro) this.querySelector('.bstore-intro').append(intro);
      features.append(source);
      this.querySelector('[data-info-open="features"]').hidden = !features.textContent.trim();
      this.onClick = event => {
        if (event.target.closest('[data-info-print]')) {
          const printFrame = document.createElement('iframe');
          printFrame.title = this.dataset.sizeLabel;
          printFrame.style.cssText = 'position:fixed;width:8.5in!important;min-width:8.5in!important;max-width:none!important;height:11in!important;min-height:11in!important;max-height:none!important;left:-10000px;border:0';
          const heading = document.createElement('h1');
          heading.textContent = 'Item Name: ' + (this.page.querySelector('h1')?.textContent.trim() || document.title);
          const storeLogo = document.querySelector('#site-header img[alt]');
          const logo = document.createElement('img');
          logo.src = storeLogo?.currentSrc || storeLogo?.src || '';
          logo.alt = 'The B. Store';
          logo.style.cssText = 'display:block;width:110px;height:auto;margin:0';
          const printContent = size.cloneNode(true);
          printContent.querySelectorAll('script,button,.bstore-fit-guide__eyebrow,.bstore-category-guide__eyebrow').forEach(el => el.remove());
          printContent.querySelectorAll('h1,h2,h3,h4,h5,h6,p').forEach(el => {
            if (/^size\s*guide\s*:?$/i.test(el.textContent.trim())) el.remove();
          });
          const methodLabel = this.methodLabel;
          printContent.querySelector('.bstore-method-summary')?.remove();
          const measurementFields = document.createElement('div');
          measurementFields.className = 'worksheet-lines';
          const guideLabels = Array.from(printContent.querySelectorAll('.bstore-fit-guide__steps dt,.bstore-category-guide__points dt'));
          const tableLabels = Array.from(printContent.querySelector('table tr')?.children || []).filter(el => !/size|stretch/i.test(el.textContent));
          (guideLabels.length ? guideLabels : tableLabels).forEach(el => {
            const field = document.createElement('p');
            const label = el.textContent.trim().replace(/^(\d{2})/, '$1 · ');
            field.textContent = `${label}: ____________________`;
            measurementFields.append(field);
          });
          printFrame.onload = async () => {
            await Promise.all(Array.from(printFrame.contentDocument.images).map(img => img.decode().catch(() => {})));
            printFrame.contentWindow.addEventListener('afterprint', () => printFrame.remove(), {once:true});
            const printBody = printFrame.contentDocument.body;
            const pageHeight = 10.95 * 96;
            if (printBody.scrollHeight > pageHeight) printBody.style.zoom = String(pageHeight / printBody.scrollHeight);
            if (window.__bstoreAuditPreview) return;
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
          };
          printFrame.srcdoc = `<!doctype html><html><head><meta charset="utf-8"><title>Size guide</title><style>
            @page{size:Letter portrait;margin:0}*{box-sizing:border-box}body{margin:0;padding:.45in;font:10pt/1.28 Arial,sans-serif;color:#111;background:#fff}h1{font-size:16pt;margin:3px 0}h2,h3,h4{font-size:12pt;color:#111;margin:7px 0 4px}p{margin:4px 0}table{border-collapse:collapse;width:100%;font-size:9pt;margin:8px 0}th,td{border:1px solid #777;padding:3px 5px;text-align:left}tr,svg,.bstore-fit-guide__steps>div{break-inside:avoid}thead{display:table-header-group}svg{display:block;height:190px;max-width:100%;margin:auto}.bstore-fit-guide__grid{display:grid;grid-template-columns:32% minmax(0,1fr);gap:8px;align-items:center;margin:7px 0}.bstore-fit-guide__steps{margin:0}.bstore-fit-guide__steps dt{font-weight:bold}.bstore-fit-guide__steps dt span{margin-right:8px}.bstore-fit-guide__steps dd{margin:0 0 6px}.bstore-fit-guide__eyebrow{font-size:8pt;letter-spacing:1px}.bstore-fit-guide__drawing p{font-size:8pt;text-align:center}.bstore-fit-guide__example{border-top:1px solid;padding-top:4px}.bstore-fit-guide__example p{display:inline;margin-left:10px}.bstore-fit-guide__note{font-size:9pt}img{max-width:100%;height:auto}a{color:#111}.worksheet-brand-header{display:flex;align-items:center;justify-content:flex-start;gap:22px;border-bottom:1px solid #777;padding-bottom:10px;margin-bottom:10px}.worksheet-title{font-size:23pt;font-weight:700;line-height:1.1;letter-spacing:.03em}.worksheet-notes{break-inside:avoid;margin-top:10px}.worksheet-lines{display:grid;grid-template-columns:1fr 1fr;gap:8px 16px}.worksheet-notes-layout{display:grid;grid-template-columns:minmax(0,1fr) 1.35in;gap:18px;align-items:start}.worksheet-size{width:1.35in;height:1.35in;border:1.5px solid #48273f;padding:10px;margin:0;font-size:12pt;font-weight:bold;text-align:center;break-inside:avoid}.worksheet-notes-layout .worksheet-lines{grid-template-columns:1fr;gap:4px}.worksheet-writing-line{display:inline-block;width:80%;border-bottom:1px solid #777;margin-top:.55in}.worksheet-help{border-top:1px solid #555;margin-top:12px;padding-top:8px;font-size:11pt;font-weight:bold;break-inside:avoid}
          .bstore-category-guide{margin:7px 0;padding:8px;border:1px solid #bbb}.bstore-category-guide__grid{display:grid;grid-template-columns:30% minmax(0,1fr);gap:10px;align-items:center}.bstore-category-guide svg{height:160px}.bstore-category-guide__points{margin:0}.bstore-category-guide__points>div{margin:4px 0}.bstore-category-guide__points dt{font-size:9pt;font-weight:bold}.bstore-category-guide__points dt span{margin-right:6px}.bstore-category-guide__points dd{font-size:8pt;margin:2px 0}.bstore-category-guide__method,.bstore-category-guide__drawing p{font-size:8pt}.bstore-category-guide__drawing p{text-align:center}</style></head><body><header class="worksheet-brand-header">${storeLogo ? logo.outerHTML : '<p>The B. Store</p>'}<div class="worksheet-title">SIZE GUIDE WORKSHEET</div></header>${heading.outerHTML}<h2 class="worksheet-method-heading">Measurement Method: ${methodLabel}</h2>${printContent.innerHTML}<section class="worksheet-notes"><h2>${this.measurementMethod === 'body' ? 'My body measurements &amp; notes' : this.measurementMethod.startsWith('garment_') ? 'My comparison garment measurements' : 'My measurements &amp; notes'}</h2><div class="worksheet-notes-layout"><div>${measurementFields.outerHTML}<p>Notes: ____________________________________</p></div><div class="worksheet-size">My size is:<br><span class="worksheet-writing-line"></span></div></div></section><footer class="worksheet-help">Still have questions? Call us at 239-919-3446 and we will be happy to help.</footer></body></html>`;
          document.body.append(printFrame);
          return;
        }

        const trigger = event.target.closest('[data-info-open]');
        if (trigger && this.page.contains(trigger)) {
          const dialog = this.querySelector(`[data-info-dialog="${trigger.dataset.infoOpen}"]`);
          if (!dialog) return;
          this.returnFocus = trigger;
          this.savedY = window.scrollY;
          this.savedOverflow = document.documentElement.style.overflow;
          document.documentElement.style.overflow = 'hidden';
          dialog.showModal();
        }
        const close = event.target.closest('[data-info-close]');
        if (close && this.contains(close)) close.closest('dialog').close();
      };
      this.page.addEventListener('click', this.onClick);
      this.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('click', event => { if (event.target === dialog) { const box = dialog.getBoundingClientRect(); if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close(); } });
        dialog.addEventListener('close', () => {
          document.documentElement.style.overflow = this.savedOverflow || '';
          this.returnFocus?.focus({preventScroll:true});
          window.scrollTo({top:this.savedY,behavior:'instant'});
        });
      });
      const sync = () => {
        this.page.querySelectorAll('.bstore-product-details').forEach(el => { el.hidden = true; });
        if (!this.hasSize) return;
        this.page.querySelectorAll('.product-variant__name, .product-variant > label').forEach(label => {
          if (!/\b(size|taille|talla|größe)\b/i.test(label.textContent)) return;
          let link = label.parentElement.querySelector('[data-info-open="size"]');
          if (!link) {
            link = document.createElement('button');
            link.type = 'button';
            link.className = 'bstore-info-link bstore-size-link';
            link.dataset.infoOpen = 'size';
            link.textContent = this.dataset.sizeLabel;
            label.before(link);
          }
          const orderHelp = label.parentElement.querySelector('.bstore-size-order-help');
          if (orderHelp && link.nextElementSibling !== orderHelp) link.after(orderHelp);
        });
        // A sold-out variant picker may exist in hidden markup.
        const visiblePickerLink = Array.from(this.page.querySelectorAll('[data-info-open="size"]'))
          .some(link => !this.contains(link) && link.getClientRects().length > 0);
        let fallback = this.querySelector('[data-info-fallback-size]');
        if (!fallback && !visiblePickerLink) {
          fallback = document.createElement('button');
          fallback.type = 'button';
          fallback.className = 'bstore-info-link bstore-size-link';
          fallback.dataset.infoOpen = 'size';
          fallback.dataset.infoFallbackSize = '';
          fallback.textContent = this.dataset.sizeLabel;
          this.querySelector('[data-info-open="features"]').after(fallback);
        }
        if (fallback) fallback.hidden = visiblePickerLink;
      };
      sync();
      this.observer = new MutationObserver(sync);
      this.observer.observe(this.page, {childList:true,subtree:true});
      const panel = this.closest('.box__product-content');
      this.updatePanel = () => { if (panel) panel.style.setProperty('--bstore-sticky-top', `${Math.min(20, window.innerHeight - panel.offsetHeight - 24)}px`); };
      this.panelObserver = new ResizeObserver(this.updatePanel);
      if (panel) this.panelObserver.observe(panel);
      window.addEventListener('resize', this.updatePanel);
      this.updatePanel();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.panelObserver?.disconnect();
      window.removeEventListener('resize', this.updatePanel);
      this.page?.removeEventListener('click', this.onClick);
      if (this.querySelector('dialog[open]')) document.documentElement.style.overflow = this.savedOverflow || '';
      this.ready = false;
    }
  });
}



/* Keep purchase controls together without changing their form association. */
(() => {
  function arrangeQuantity() {
    document.querySelectorAll('product-page').forEach(page => {
      const quantity = page.querySelector('.bstore-quantity-select');
      const select = quantity?.querySelector('select');
      const size = Array.from(page.querySelectorAll('.product-variant')).find(el => el.dataset.name?.includes('product-size-'));
      if (!size || !select || quantity.dataset.arranged) return;
      const form = select.form;
      if (!form?.getAttribute('id')) return;
      select.setAttribute('form', form.getAttribute('id'));
      quantity.addEventListener('change', event => event.stopPropagation());
      size.classList.add('bstore-size-quantity-row');
      quantity.dataset.arranged = 'true';
      size.append(quantity);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arrangeQuantity);
  else arrangeQuantity();
})();

